import type { StatoConferma, StatoPercorso, StatoWebinar } from '@aulaviva/shared';
import { Badge, Button, TextField, type VarianteBadge } from '@aulaviva/ui';
import { CircleCheck, CircleDashed, CircleHelp, CircleX, Flag, Radio, Search, Send } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { admin } from '@/testi';

export function BadgePercorso({ stato }: { stato: StatoPercorso }) {
  const map: Record<StatoPercorso, { v: VarianteBadge; icona: typeof Flag }> = {
    bozza: { v: 'neutral', icona: CircleDashed },
    pubblicato: { v: 'success', icona: CircleCheck },
    chiuso: { v: 'neutral', icona: Flag },
  };
  return (
    <Badge variant={map[stato].v} icon={map[stato].icona}>
      {admin.statiPercorso[stato]}
    </Badge>
  );
}

export function BadgeWebinar({ stato }: { stato: StatoWebinar }) {
  const map: Record<StatoWebinar, { v: VarianteBadge; icona: typeof Flag }> = {
    bozza: { v: 'neutral', icona: CircleDashed },
    pubblicato: { v: 'success', icona: Send },
    in_diretta: { v: 'accent', icona: Radio },
    test_aperto: { v: 'warning', icona: CircleHelp },
    chiuso: { v: 'neutral', icona: Flag },
  };
  return (
    <Badge variant={map[stato].v} icon={map[stato].icona}>
      {admin.statiWebinar[stato]}
    </Badge>
  );
}

export function BadgeConferma({ stato }: { stato: StatoConferma }) {
  const map: Record<StatoConferma, { v: VarianteBadge; icona: typeof Flag }> = {
    invitato: { v: 'warning', icona: CircleHelp },
    confermato: { v: 'success', icona: CircleCheck },
    rinunciato: { v: 'error', icona: CircleX },
  };
  return (
    <Badge variant={map[stato].v} icon={map[stato].icona}>
      {admin.statiConferma[stato]}
    </Badge>
  );
}

/** Campo di ricerca con ritardo di 300 ms, per non chiamare l'API a ogni tasto. */
export function CampoRicerca({ valore, onCambia, label = admin.comuni.cerca, hint }: { valore: string; onCambia: (v: string) => void; label?: string; hint?: string }) {
  const [locale, setLocale] = useState(valore);
  useEffect(() => {
    const t = setTimeout(() => {
      if (locale !== valore) onCambia(locale);
    }, 300);
    return () => clearTimeout(t);
  }, [locale, valore, onCambia]);
  return (
    <TextField
      label={label}
      hint={hint}
      type="search"
      value={locale}
      onChange={(e) => setLocale(e.target.value)}
      trailing={<Search aria-hidden size={20} strokeWidth={1.75} className="text-ink-muted mr-3" />}
    />
  );
}

/** Intestazione di pagina admin con azione primaria a destra. */
export function IntestazioneAdmin({ titolo, sottotitolo, azione, sopra }: { titolo: ReactNode; sottotitolo?: ReactNode; azione?: ReactNode; sopra?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      {sopra}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="t-title-1">{titolo}</h1>
          {sottotitolo && <p className="t-body text-ink-muted">{sottotitolo}</p>}
        </div>
        {azione && <div className="shrink-0">{azione}</div>}
      </div>
    </div>
  );
}

export function LinkIndietro({ to, testo }: { to: string; testo: string }) {
  return (
    <Link to={to} className="t-label no-underline text-ink-muted hover:text-ink">
      ← {testo}
    </Link>
  );
}

export function BottoneCopia({ testo, etichetta, onCopiato }: { testo: string; etichetta: string; onCopiato: () => void }) {
  return (
    <Button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(testo);
          onCopiato();
        } catch {
          /* appunti non disponibili: il testo resta leggibile accanto */
        }
      }}
    >
      {etichetta}
    </Button>
  );
}
